import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './PeupleQcCombinedHeader.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaSearch, FaComments, FaVideo, FaBell, FaShoppingCart, FaTasks } from 'react-icons/fa';
import { CustomImage } from './CustomImage';
import LiberiumLink from './LiberiumLink';
import { useNavigate } from './Router';
import { Loi25 } from "./Loi25";

const PeupleQcCombinedHeader = ({ user, children, activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const [userConnected, setUserConnected] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [virtualTaskCount, setVirtualTaskCount] = useState(0);
  
  // IDs des utilisateurs autorisés à voir les tâches virtuelles
  const allowedUserIds = [2, 3, 54, 1435];
  const canSeeVirtualTasks = user && allowedUserIds.includes(user.user_id);

  function getMobileOperatingSystem() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/windows phone/i.test(userAgent)) {
      return "Windows Phone";
    }

    if (/android/i.test(userAgent)) {
      return "Android";
    }

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    }

    return "unknown";
  }

  useEffect(() => {
    async function fetchData() {
      if (user) {
        const messageCount = await sdk.models.SocialMessage.countUnread({});
        setUnreadMessagesCount(messageCount.count);
        
        const notifications = await sdk.models.SocialNotification.unseenCount({});
        setNotificationCount(notifications.count);
        
        // Charger le nombre de tâches virtuelles seulement pour les utilisateurs autorisés
        if (canSeeVirtualTasks) {
          const storedActiveAccount = localStorage.getItem('activeVirtualAccount');
          const os = getMobileOperatingSystem();
          const taskCount = await sdk.models.VirtualTask.countTask({
            os: os,
            activeAccount: storedActiveAccount
          });
          setVirtualTaskCount(taskCount.count || 0);
        }
      }
    }
    
    if (typeof user !== 'undefined' && user !== null) {
      setUserConnected(true);
      fetchData();
    }
    
    setLoaded(true);
  }, [user, canSeeVirtualTasks]);

  useEffect(() => {
    const onMessageReceivedHandler = (message) => {
      try {
        if (typeof message.tap !== 'undefined' && message.tap === 'background') {
          if (typeof message.path !== 'undefined') {
            sdk.models.SocialNotification.setTapped({url: message.path});
            return navigate(message.path);
          }
        }
        if (typeof message.url !== 'undefined') {
          sdk.models.SocialNotification.setTapped({url: message.url});
          return navigate(message.url);
        }
        if (message.messageType === 'notification') {
          console.log('handleNotificationMessage');
        } else {
          console.log('handleDataMessage(message)');
        }
      } catch (e) {
        console.log('Exception in onMessageReceived callback: ' + e.message);
      }
    };

    const onErrorHandler = (error) => {
      console.log('Failed receiving FirebasePlugin message', error);
    };

    if (typeof window.FirebasePlugin !== 'undefined') {
      window.FirebasePlugin.onMessageReceived(onMessageReceivedHandler, onErrorHandler);
    }

    // Cleanup function to remove the listener when the component is unmounted
    return () => {
      if (typeof window.FirebasePlugin !== 'undefined') {
        window.FirebasePlugin.onMessageReceived(null, null);
      }
    };
  }, [navigate]);

  const handleNavClick = (tab) => {
    if (setActiveTab) {
      setActiveTab(tab);
    }
  };

  return (
    <>
      <div className="combined-header">
        {/* Logo à gauche */}
        <div className="combined-header-brand" onClick={() => handleNavClick('home')}>
          {loaded && (
            <img
              src="https://api.liberium.net/s/liberium-public-img/peuple_qc_ico_transparent.png"
              alt="PeupleQc Logo"
              className="logo-img"
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>

        {/* Navigation à droite */}
        <div className="combined-nav">
          {userConnected && (
            <>
              {/* Tâches virtuelles - Visible seulement pour certains utilisateurs */}
              {canSeeVirtualTasks && (
                <div className="combined-nav-item">
                  <LiberiumLink href="/virtualJob">
                    <div className="combined-nav-link">
                      <FaTasks />
                      {virtualTaskCount > 0 && (
                        <span className="notification-badge">{virtualTaskCount}</span>
                      )}
                    </div>
                  </LiberiumLink>
                </div>
              )}
              
              {/* Videos */}
              <div className="combined-nav-item">
                <div
                  className={`combined-nav-link ${activeTab === 'videos' ? 'active' : ''}`}
                  onClick={() => navigate('/videos')}
                >
                  <FaVideo />
                </div>
              </div>
              
              {/* Notifications */}
              <div className="combined-nav-item">
                <div
                  className={`combined-nav-link ${activeTab === 'notifications' ? 'active' : ''}`}
                  onClick={() => handleNavClick('notifications')}
                >
                  <FaBell />
                  {notificationCount > 0 && (
                    <span className="notification-badge">{notificationCount}</span>
                  )}
                </div>
              </div>
              
              {/* Recherche */}
              <div className="combined-nav-item">
                <LiberiumLink href="/search">
                  <div className="combined-nav-link">
                    <FaSearch />
                  </div>
                </LiberiumLink>
              </div>

              {/* Messages */}
              <div className="combined-nav-item">
                <LiberiumLink href="/messengers">
                  <div className="combined-nav-link">
                    <FaComments />
                    {unreadMessagesCount > 0 && (
                      <span className="notification-badge">{unreadMessagesCount}</span>
                    )}
                  </div>
                </LiberiumLink>
              </div>

              {/* Profil */}
              <div className="combined-nav-item">
                <div
                  className={`combined-nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => handleNavClick('profile')}
                >
                  {user && user.avatar ? (
                    <CustomImage 
                      src={`https://peupleqc.com/s/peupleqc-upload/${user.avatar}`}
                      alt="Profile"
                      className="profile-image"
                    />
                  ) : (
                    <div className="profile-placeholder"></div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="children-wrapper">{children}</div>
      <Loi25/>
    </>
  );
};

export { PeupleQcCombinedHeader };